import * as React from 'react';
import { File, UploadCloud, X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Button } from './button';

interface FileUploadProps {
  value?: File[] | null;
  onChange?: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  maxSize?: number;
  hint?: string;
}

export function FileUpload({
  value,
  onChange,
  accept,
  multiple = false,
  disabled,
  className,
  maxSize,
  hint,
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const files = value ?? [];

 
  const allowedExtensions = (accept ?? '')
    .split(',')
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t.startsWith('.'));

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = Array.from(incoming);
    if (allowedExtensions.length > 0) {
      const invalid = next.find((f) => {
        const name = f.name.toLowerCase();
        return !allowedExtensions.some((ext) => name.endsWith(ext));
      });
      if (invalid) {
        const label = allowedExtensions.map((e) => e.slice(1).toUpperCase()).join(', ');
        setError(`${invalid.name} is not allowed. Only ${label} files are accepted.`);
        return;
      }
    }
    if (maxSize) {
      const tooBig = next.find((f) => f.size > maxSize);
      if (tooBig) {
        setError(`${tooBig.name} exceeds ${(maxSize / 1024 / 1024).toFixed(1)} MB limit`);
        return;
      }
    }
    setError(null);
    onChange?.(multiple ? [...files, ...next] : next.slice(0, 1));
  };

  const removeAt = (idx: number) => {
    onChange?.(files.filter((_, i) => i !== idx));
  };

  return (
    <div className={className}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input bg-background p-6 text-center transition-colors hover:border-ring',
          dragOver && 'border-primary bg-accent',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <UploadCloud className="h-5 w-5" />
        </div>
        <div className="space-y-0.5">
          <p className="text-sm font-medium">Drop files here or click to browse</p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {files.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm"
            >
              <File className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{f.name}</p>
                <p className="text-xs text-muted-foreground">{formatBytes(f.size)}</p>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeAt(idx)}
                disabled={disabled}
                aria-label={`Remove ${f.name}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
